import TextMetric from '@/atoms/metrics/TextMetric';
import Heading from '@/atoms/typography/Heading';
import Tooltip from '@/molecules/modals/Tooltip/Tooltip';
import getComparisonChange from '@/organisms/charts/utils/getComparisonChange';
import isPercentageMetric from '@/organisms/charts/utils/isPercentageMetric';
import MetricProperty from '@enums/MetricProperty';
import Size from '@enums/Size';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import format from '@pkg/utils/numbers/format';
import { EntityMetric } from '@/lib/enums';
import { brand } from '@/lib/theme/tokens/palettes';
import TooltipLabel from './TooltipLabel';
import formatDifference from './utils/formatDifference';
import getCountLabel from './utils/getCountLabel';

const ChartTooltip = ({
  absolute = false,
  children,
  maxWidth,
  minWidth,
  metric,
  metricTotals,
  position = 'top',
  item,
}) => {
  const { data, comparison } = item;
  const isRelativePercentage = metric === EntityMetric.RELATIVE_PERCENTAGE;
  const isEntityPercentage = metric === EntityMetric.ENTITY_PERCENTAGE;
  const isSpan = metric === EntityMetric.SPAN;
  const percentageMetric = isPercentageMetric(metric);
  const baseMetric = percentageMetric ? EntityMetric.HOURS : metric;
  const metricValue = data[baseMetric];
  const displayMetricValue = isSpan ? data[EntityMetric.MANAGERS] : metricValue;

  const formatValue =
    (isEntityPercentage || isRelativePercentage) && item.parentData
      ? data[metric] / 100
      : metricTotals?.[baseMetric]
        ? metricValue / metricTotals[baseMetric]
        : 0;

  const percentage = metricTotals?.[baseMetric]
    ? format(formatValue, {
        maximumFractionDigits: 2,
        style: 'percent',
      })
    : null;

  const comparisonPercentage = formatDifference(
    getComparisonChange(item, metric, metricTotals, absolute),
    { metric, absolute }
  );

  const metricLabel =
    displayMetricValue > 1
      ? MetricProperty[isSpan ? EntityMetric.MANAGERS : baseMetric].label.plural
      : MetricProperty[isSpan ? EntityMetric.MANAGERS : baseMetric].label
          .singular;

  const metricFormatOptions = MetricProperty[baseMetric].formatOptions;

  const comparisonColor = comparison?.color;

  const countLabel = getCountLabel(item.type, data.count);

  return (
    <Tooltip
      color="secondary"
      minWidth={minWidth}
      maxWidth={maxWidth}
      position={position}
      title={
        <Stack
          alignItems={'center'}
          direction="row"
          justifyContent="space-between"
          spacing={1.5}
          py={0.5}
          width="100%"
        >
          <Box>
            <TooltipLabel item={item} />
          </Box>
          <Stack direction="row" alignItems="stretch" spacing={1} pl={2}>
            {metric !== EntityMetric.COUNT && (
              <TextMetric
                alignItems="center"
                justifyContent="center"
                boldText
                label={countLabel}
                size={Size.XX_SMALL}
                value={data.count}
              />
            )}
            <TextMetric
              alignItems="center"
              justifyContent="center"
              boldText
              label={metricLabel}
              formatOptions={metricFormatOptions}
              size={Size.XX_SMALL}
              value={displayMetricValue}
            />
            {percentage !== null && (
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  backgroundColor: brand.lightBlue.tints[2],
                  borderRadius: '4px',
                  px: 1,
                }}
              >
                <Heading variant="h6" overrideStyles={{ fontWeight: 600 }}>
                  {percentage}
                </Heading>
              </Stack>
            )}
            {comparisonPercentage && (
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  backgroundColor: brand.lightBlue.tints[2],
                  borderRadius: '4px',
                  px: 1,
                }}
              >
                <Heading
                  variant="h6"
                  overrideStyles={{
                    fontWeight: 600,
                    color: comparisonColor?.text,
                  }}
                >
                  {comparisonPercentage}
                </Heading>
              </Stack>
            )}
          </Stack>
        </Stack>
      }
    >
      {children}
    </Tooltip>
  );
};

export default ChartTooltip;
